import {
   API_ENDPOINTS,
   checkTheme,
   DataColumnInterface,
   DataTable,
   DateFormats,
   FormInput,
   formatDateFns,
   ImageType,
   PageLayout,
   replaceKeyWithValue,
   Roles,
   route,
   StringHelpers,
   Themes,
   useAuthorisationContext,
   useLangContext,
} from 'carrier-fe';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WarrantyListItem } from '../../types/warranty';
import { format, formatDate, subMonths } from 'date-fns';

function Warranties() {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const { hasRoleAccess } = useAuthorisationContext();
   const { crud, fields, models } = useLangContext();

   const title = useMemo(
      () =>
         StringHelpers.title(
            replaceKeyWithValue(
               crud?.pages?.index.title || ':model List ',
               'model',
               crud?.models?.warranty || 'Warranty'
            )
         ),
      [crud, models]
   );

   const handleOptionSelect = (
      action: string | ImageType | null,
      data: WarrantyListItem
   ) => {
      const installationId = checkTheme(Themes.Toshiba)
         ? data.toshiba_installation_id
         : data.viessmann_installation_id;

      const actions: { [key: string]: () => void } = {
         view: () => {
            const searchFilter = searchParams.get('filter[search]');
            const baseUrl = `/installation/${installationId}?from=warranties`;
            navigate(
               searchFilter
               ? `${baseUrl}&filter[search]=${encodeURIComponent(searchFilter)}`
               : baseUrl
            );
         },
      };

      if (typeof action === 'string' && actions[action]) {
         actions[action]();
      }
   };

   const colMapping = (data: WarrantyListItem): DataColumnInterface[] => {
      const actionsColumn: DataColumnInterface = {
         id: 'actions',
         label: StringHelpers.title(fields?.actions || 'Actions'),
         value: data.permissions.view ? (
            <FormInput
               type="select"
               label={StringHelpers.title(fields?.actions || 'Action')}
               placeholder={StringHelpers.title(fields?.select || 'Select')}
               name="actions"
               value=""
               onChange={(value: any) => {
                  handleOptionSelect(value, data);
               }}
               options={[
                  {
                     label: StringHelpers.title(
                        crud?.view || 'View'
                     ),
                     value: 'view',
                  },
               ]}
               noMargin
            />
         ) : null,
      };

      if (checkTheme(Themes.Toshiba)) {
         const toshibaColumns: DataColumnInterface[] = [
            {
               id: 'installer_company_name',
               label: crud?.sub_titles?.installer
                  || 'Installer',
               value: data?.installer_company_name
                  || '-',
               sortable: true,
               visibleCondition: () =>
                  hasRoleAccess(Roles.PLATFORM_MANAGER)
                  || hasRoleAccess(Roles.DISTRIBUTION_MANAGER),
            },
            {
               id: 'distributor_name',
               label: crud?.sub_titles?.distributor
                  || 'Distributor',
               value: data?.distributor_name
                  || '-',
               sortable: true,
            },
            {
               id: 'engineer_name',
               label: crud?.models?.engineer
                  || 'Engineer',
               value: data?.engineer_name
                  || '-',
               sortable: true,
            },
            {
               id: 'created_at',
               label: StringHelpers.title(
                  fields?.created_at
                  || 'Created Date'
               ),
               value: formatDateFns(data?.created_at || null, DateFormats.DATETIME_SHORT)
                  || '-',
               sortable: true,
            },
            actionsColumn,
         ];

         return toshibaColumns;
      }

      const viessmannColumns: DataColumnInterface[] = [
         {
            id: 'company_name',
            label: StringHelpers.title(fields?.name || 'Name'),
            value: data?.company_name || '-',
            sortable: true,
         },
         {
            id: 'appliance',
            label: StringHelpers.title(fields?.appliance_name || 'appliance name'),
            value: data?.appliance || '-',
            sortable: true,
         },
         {
            id: 'engineer_name',
            label: StringHelpers.title(fields?.engineer || 'Engineer'),
            value: data?.engineer_name || '-',
            sortable: true,
         },
         {
            id: 'customer_name',
            label: StringHelpers.title(crud?.models?.customer || 'Customer'),
            value: data?.customer_name || '-',
            sortable: true,
         },
         {
            id: 'start_date',
            label: StringHelpers.title(fields?.start_date || 'Start date'),
            value: format(new Date(data.start_date!), DateFormats.DATE) || '-',
            sortable: true,
         },
         {
            id: 'end_date',
            label: StringHelpers.title(fields?.end_date || 'End date'),
            value: format(new Date(data.end_date!), DateFormats.DATE) || '-',
            sortable: true,
         },
         {
            id: 'price',
            label: `${StringHelpers.title(fields?.price || 'price')} £`,
            value: Number(data?.price).toFixed(2) || '',
            sortable: true,
         },
         actionsColumn,
      ];

      return viessmannColumns;
   };

   const viessmannFilters = [
      {
         name: 'start_date',
         label: StringHelpers.title(
            fields?.start_date || 'Start Date'
         ),
         type: 'date',
         value: formatDate(
            subMonths(new Date(), 3).setHours(0, 0, 0, 0),
            DateFormats.DB
         ),
      },
   ];

   return (
      <PageLayout title={title || 'Warranty List'}>
         <DataTable<WarrantyListItem>
            routeEndpoint={route(API_ENDPOINTS.REGISTRATION.WARRANTY.INDEX)}
            colMapping={colMapping}
            incrementPaginationButtons
            filters={checkTheme(Themes.Viessmann) ? viessmannFilters : undefined}
         />
      </PageLayout>
   );
}

export default Warranties;
